import { eq, option, ord } from 'fp-ts';
import { constant, flow, pipe } from 'fp-ts/function';
import * as N from 'fp-ts/number';
import * as iots from 'io-ts';
export * from 'fp-ts/number';
/**
 * Compare two `Number`s and return `true` if they are equal up to the given (absolute) precision.
 * Note that this is not scale invariant, i.e. an Eq derived using this function will not behave
 * the same way for large numbers as it does for tiny numbers.
 *
 * We intentionally don't provide a scale invariant version of getEq, because in our context it's
 * important to define specific Eqs for specific situations. We compare results with a relatively
 * low precision, for example.
 */
export const getEqAbsolute = (tolerance) => eq.fromEquals((a, b) => Math.abs(a - b) <= tolerance);
/**
 * Clamp a number to the given bounds.
 *
 * @example
 * clamp(1, 3)(5) // => 3
 */
export const clamp = ord.clamp(N.Ord);
/**
 * Asserts that a value is a number we can work with, i.e. it is not nullable, Infinity, or NaN.
 *
 * TypeScript can't add a type guard for this, because `typeof NaN === 'number'`. But we take the
 * liberty to add one anyways, because it is useful in our context.
 */
export const isFinite = (x) => Number.isFinite(x);
/**
 * Remove trailing zeroes from a numeric string.
 *
 * Working on a string is more robust than other solutions like `parseFloat` that have many
 * edge cases.
 *
 * @example
 * stripTrailingZeroes("0.0000") // => "0"
 * stripTrailingZeroes("0.0300") // => "0.03"
 */
export const stripTrailingZeroes = (x) => x.replace(/(\.[0-9]*[1-9])0+$|\.0*$/, '$1');
/**
 * A data last version of {@link Number.toFixed}. Clamps the digits to appear after the decimal
 * point to the required [0, 20] range.
 */
export const toFixed = flow(clamp(0, 20), (fractionDigits) => (x) => x.toFixed(fractionDigits));
/**
 * Format a number to have up to `fractionDigits` digits after the decimal point, but remove
 * trailing zeroes.
 */
export const toFixedZ = (fractionDigits) => flow(toFixed(fractionDigits), stripTrailingZeroes);
export const unitInterval = iots.brand(iots.number, (n) => ord.between(N.Ord)(0, 1)(n), 'UnitInterval');
/**
 * Force a number to be within the unit interval [0, 1].
 */
export const clampUnitInterval = (x) => clamp(0, 1)(x);
/**
 * Map a number from the domain [min, max] to a number in the unit interval [0, 1]. Numbers
 * outside the range are {@link clamp}ed to the domain's boundaries.
 *
 * @throws {TypeError} if min, max, or num is NaN
 */
export const unitIntervalFromRange = (min, max) => {
    if (Number.isNaN(min))
        throw new TypeError('min is NaN');
    if (Number.isNaN(max))
        throw new TypeError('max is NaN');
    return (num) => {
        if (Number.isNaN(num))
            throw new TypeError('num is NaN');
        if (min === max)
            return 0;
        return pipe(num, min < max ? clamp(min, max) : clamp(max, min), (x) => (x - min) / (max - min), Math.abs, clampUnitInterval);
    };
};
/**
 * Map a number from the unit interval to a number in the codomain [min, max]. Numbers outside
 * the unit interval are clamped to [0, 1].
 */
export const unitIntervalToRange = (min, max) => flow(clampUnitInterval, (x) => {
    const range = max - min;
    return min + x * range;
});
/**
 * Safely create a {@link UnitInterval} from a numerator and denominator. Clamps to [0, 1] should
 * the numerator be greater than the denominator.
 */
export const unitIntervalFromParts = (numerator, denominator) => pipe(numerator / denominator, option.fromPredicate(isFinite), option.map(clampUnitInterval));
/**
 * Variant of {@link unitIntervalFromParts} that returns a fallback value should the resulting
 * value not be finite.
 */
export const unitIntervalFromPartsOrElse = (orElse) => flow(unitIntervalFromParts, option.getOrElse(constant(clampUnitInterval(orElse))));
/**
 * Create a {@link Percent} value out of a {@link UnitInterval}.
 */
export const percentFromUnitInterval = (x) => (x * 100);
/**
 * Safely create a {@link Percent} from a numerator and denominator. Clamps to [0, 100] should
 * the numerator be greater than the denominator.
 */
export const percentFromParts = flow(unitIntervalFromParts, option.map(percentFromUnitInterval));
/**
 * Variant of {@link percentFromParts} that returns a fallback value should the resulting
 * value not be finite.
 */
export const percentFromPartsOrElse = (orElse) => flow(percentFromParts, option.getOrElse(constant(orElse)));
/**
 * Use this function in places where you are certain that the denominator is non-zero.
 *
 * @throws {TypeError} If the denominator is zero.
 */
export const unsafePercentFromParts = flow(percentFromParts, option.getOrElse(() => {
    throw new TypeError('Division by zero.');
}));
// TODO replace with fp-ts-std
export const add = (b) => (a) => a + b;
// TODO replace with fp-ts-std
export const subtract = (b) => (a) => a - b;
//# sourceMappingURL=number.js.map